<script setup lang="ts">
import { EColors, type Colors } from '@/types/constants/ColorValues'
import { useDebounceFn } from '@vueuse/core'
import { computed, ref, type InputHTMLAttributes, type PropType } from 'vue'
import { EValidationMessageLabels } from '../../types/enum/ValidationMessageEnum'
import { EDebounceTime } from '../../utils/debounceUtils'
import CustomField from './CustomField.vue'

const props = defineProps({
    label: { type: String, required: false },
    type: Object as PropType<Colors>,
    placeholder: { type: String },
    modelValue: String,
    message: String,
    required: Boolean,
    inputType: String as PropType<InputHTMLAttributes['type']>,
    hasError: Boolean,
    errorText: String
})
type Emits = {
    (e: 'change', value?: string): void
}
const emit = defineEmits<Emits>()

const isLoading = ref(false)

const localValue = computed({
    get: () => props.modelValue,
    set: value => {
        isLoading.value = true
        debouncedOrganization(value ?? undefined)
    }
})

const debouncedOrganization = useDebounceFn((value?: string) => {
    emit('change', value)
    isLoading.value = false
}, EDebounceTime.DEFAULT)

const computedType = computed(() =>
    props.hasError ? EColors.DANGER : props.type ?? EColors.PRIMARY
)

const inputRef = ref<HTMLSpanElement | null>(null)

defineExpose({ inputRef })
</script>

<template>
    <CustomField
        :label="label ?? ''"
        :message="errorText"
        :type="computedType"
        :required="required">
        <b-input
            ref="inputRef"
            :placeholder="placeholder ?? label"
            :type="inputType"
            :icon-right="localValue?.length ? 'circle-xmark' : undefined"
            icon-right-clickable
            :loading="isLoading"
            @icon-right-click="localValue = undefined"
            :validation-message="inputType ? EValidationMessageLabels(inputType) : undefined"
            v-model="localValue"
            v-bind="$attrs"
            custom-class="placeholder" />
    </CustomField>
</template>

<style lang="scss">
.placeholder::placeholder {
    color: var(--color-placeholder);
}

.textarea {
    background-color: var(--color-background-secondary);
    color: var(--color-text);
}
</style>

<!-- :icon-right="modelValue?.length ? 'circle-xmark' : undefined"
            @icon-right-click="modelValue = undefined" -->
